/** Header widgets: one-tap Clock in/out + compliance dot with pending items popover. */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { LogIn, LogOut, CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { collectPunchMeta } from "@/lib/punchMeta";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

const DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };

export default function HeaderHrWidgets() {
  const { activeWorkspaceId: wsId } = useAuth();
  const nav = useNavigate();
  const [today, setToday] = useState(null);
  const [comp, setComp] = useState(null);

  const load = useCallback(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/attendance/today`).then((r) => setToday(r.data)).catch(() => {});
    api.get(`/workspaces/${wsId}/me/compliance-summary`).then((r) => setComp(r.data)).catch(() => {});
  }, [wsId]);
  useEffect(() => {
    load();
    // Feb 2026 R281 · Visibility-gated polling.
    const stopPoll = pollWhenVisible(load, 180000);
    window.addEventListener("acq-refresh-compliance", load);
    return () => { stopPoll(); window.removeEventListener("acq-refresh-compliance", load); };
  }, [load]);

  const punch = async () => {
    const action = today?.record?.clock_in && !today?.record?.clock_out ? "clock-out" : "clock-in";
    try {
      const meta = await collectPunchMeta({ requireLocation: action === "clock-in" });
      const { data } = await api.post(`/workspaces/${wsId}/attendance/${action}`, meta);
      toast.success(action === "clock-in" ? "Clocked in — have a great day!" : `Clocked out — ${(data.work_minutes / 60).toFixed(1)}h today`);
      load();
    } catch (e) {
      if (e?.message && !e.response) toast.error(e.message);
      else toast.error(apiErrorMessage(e, "Punch failed"));
    }
  };

  const clockedIn = today?.record?.clock_in && !today?.record?.clock_out;
  const done = today?.record?.clock_out;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={done ? undefined : punch}
        disabled={!!done}
        className={`h-8 px-3 text-xs font-semibold border transition-colors ${clockedIn
          ? "border-red-500/50 text-red-600 hover:bg-red-500/10"
          : done ? "border-border text-foreground/40 cursor-default"
          : "border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10"}`}
        data-testid="header-clock-btn"
        title={done ? "Done for today" : clockedIn ? "Clock out" : "Clock in"}
      >
        {clockedIn ? <span className="flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" />Out</span>
          : done ? <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" />Done</span>
          : <span className="flex items-center gap-1.5"><LogIn className="w-3.5 h-3.5" />In</span>}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="relative h-8 w-8 border border-border flex items-center justify-center hover:bg-muted/60" data-testid="header-compliance-dot" title="My compliance">
            <ShieldAlert className="w-4 h-4 text-foreground/60" />
            <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${DOT[comp?.score] || "bg-foreground/30"}`} data-testid="compliance-score-dot" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <div className="px-3 py-2 border-b border-border text-xs font-semibold flex items-center gap-2" data-testid="compliance-popover">
            <span className={`w-2 h-2 rounded-full ${DOT[comp?.score] || "bg-foreground/30"}`} />
            My compliance {comp?.off_today ? "· day off 🎉" : comp?.on_leave_today ? "· on leave" : ""}
          </div>
          {(comp?.items || []).map((i) => (
            <div key={i.key} className="px-3 py-1.5 text-xs flex items-center gap-2" data-testid={`compliance-item-${i.key}`}>
              {i.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <AlertTriangle className={`w-3.5 h-3.5 ${i.severity === "red" ? "text-red-500" : "text-amber-500"}`} />}
              <span className={i.ok ? "text-foreground/50 line-through" : ""}>{i.label}</span>
            </div>
          ))}
          {comp && comp.pending_count === 0 && <div className="px-3 py-2 text-xs text-emerald-600">All caught up. ✨</div>}
          <DropdownMenuItem onClick={() => nav("/app/timesheet")} data-testid="compliance-goto-timesheet">Open timesheet →</DropdownMenuItem>
          <DropdownMenuItem onClick={() => nav("/app/attendance")} data-testid="compliance-goto-attendance">Open attendance →</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
